import React, { useState } from 'react';
import { Box, Card, CardContent, Grid, Link, Stack, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getStatusColor, formatRegion, formatCountry } from '../../utils/meetingUtils';
import { certaintyToGrade } from '../../utils/certaintyFormatters';
import { formatDate } from '../../utils/dateFormatters';
import { formatDateTime } from '../../utils/datetime';
import MeetingHistoryDialog from './MeetingHistoryDialog';

const ParticipantCard = styled(Card)(({ theme, status }) => ({
  backgroundColor: `${getStatusColor(status)}10`,
  border: `1px solid ${getStatusColor(status)}`,
  '&:hover': {
    borderColor: getStatusColor(status),
    boxShadow: `0 0 8px ${getStatusColor(status)}40`
  },
  '& .MuiTypography-caption': {
    fontSize: '0.7rem'
  },
  '& .property-value': {
    fontSize: '0.8rem'
  }
}));

function ParticipantCardContent({ participant, continuityData, accountName }) {
  const { zoomParticipant, pslUser, status } = participant;
  const [historyOpen, setHistoryOpen] = useState(false);
  const { t, i18n } = useTranslation('shared');

  // Three-state continuity display (only for verified participants)
  const renderContinuity = () => {
    if (status === 'unverified') return null;
    // continuityData === undefined means continuity is null (no auth / anonymous) — show nothing
    if (continuityData === undefined) return null;

    // continuityData exists with meeting_count > 0: prior meetings found
    if (continuityData && continuityData.meeting_count > 0) {
      return (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {t('participant.meetingHistory')}
          </Typography>
          <Typography className="property-value" sx={{ color: 'text.primary' }}>
            {t('participant.lastMetWith', { accountName, date: formatDate(continuityData.last_met_time), count: continuityData.meeting_count })}
          </Typography>
          <Link
            component="button"
            variant="caption"
            onClick={() => setHistoryOpen(true)}
            sx={{ mt: 0.25 }}
          >
            {t('participant.viewHistory')}
          </Link>
        </Box>
      );
    }

    // continuityData is absent from map but continuity object is not null: first meeting
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {t('participant.meetingHistory')}
        </Typography>
        <Typography className="property-value" sx={{ color: 'warning.main', fontStyle: 'italic' }}>
          {t('participant.firstMeetingWith', { accountName })}
        </Typography>
      </Box>
    );
  };

  return (
    <Grid item xs={12} sm={12} md={12}>
      <ParticipantCard status={status} sx={{ width: '100%' }}>
        <CardContent>
          <Typography gutterBottom fontWeight="bold" sx={{ mb: 2, width: '100%' }}>
            {pslUser?.full_name || zoomParticipant.screenName}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            {pslUser?.photo_verification_url && (
              <img
                src={`${pslUser.photo_verification_url}`}
                alt={`${pslUser.full_name || zoomParticipant.screenName} verification photo`}
                style={{
                  width: '80px',
                  alignSelf: 'stretch',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  flexShrink: 0
                }}
              />
            )}

            <Box sx={{ flex: 1 }}>
              <Stack spacing={1.5}>
                {pslUser?.certainty !== undefined && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('participant.biometricCertainty')}
                    </Typography>
                    <Typography className="property-value" sx={{ color: getStatusColor(status) }}>
                      {certaintyToGrade(pslUser?.certainty)}
                    </Typography>
                  </Box>
                )}

                {(pslUser?.document_type || pslUser?.issuing_country) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('participant.document')}
                    </Typography>
                    <Typography className="property-value" sx={{ color: getStatusColor(status) }}>
                      {[pslUser?.document_type, formatCountry(pslUser?.issuing_country)].filter(Boolean).join(', ')}
                    </Typography>
                  </Box>
                )}

                {pslUser?.region && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('participant.region')}
                    </Typography>
                    <Typography className="property-value" sx={{ color: getStatusColor(status) }}>
                      {formatRegion(pslUser?.region)}
                    </Typography>
                  </Box>
                )}

                {pslUser?.phone_number_verification_id && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('participant.phoneNumber')}
                    </Typography>
                    <Typography className="property-value" sx={{ color: getStatusColor(status) }}>
                      {pslUser?.phone_number}
                    </Typography>
                  </Box>
                )}

                {pslUser?.age && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('participant.age')}
                    </Typography>
                    <Typography className="property-value" sx={{ color: getStatusColor(status) }}>
                      {pslUser?.age}
                    </Typography>
                  </Box>
                )}

                {pslUser?.iat && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('participant.verified')}
                    </Typography>
                    <Typography className="property-value" sx={{ color: getStatusColor(status) }}>
                      {formatDateTime(pslUser.iat * 1000, i18n.language, {
                        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                )}

                {renderContinuity()}
              </Stack>
            </Box>
          </Box>
        </CardContent>
      </ParticipantCard>

      {historyOpen && (
        <MeetingHistoryDialog
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          polyUserId={pslUser?.poly_user_id}
          participantName={pslUser?.full_name || zoomParticipant.screenName}
          accountName={accountName}
        />
      )}
    </Grid>
  );
}

export default ParticipantCardContent;
